import { CircularProgress, MenuItem, Select } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { PNG, SVG_MUI } from "@ryde/assets";
import { MuiButton } from "@ryde/controls";
import { useRtl } from "@ryde/redux";
import { OAUTH_SCOPE_READ, OAUTH_SCOPE_WRITE, useIsMobile } from "@ryde/shared";

import {
  CopyrightLine,
  LeftImageLogin,
  MainLoginContainerStyled,
  textboxLoginSx,
} from "../login/login.page.styled";
import {
  OauthConsentFormStyled,
  OauthConsentTitleStyled,
} from "./parts/oauth-consent.styled";
import { useOauthConsent } from "./parts/use-oauth-consent";

const COMPANY_SELECT_LABEL_ID = "oauth-consent-company-label";

/**
 * OAuth 2.1 consent screen for the MCP connector (RYDE-7833). Reached via the backend
 * GET /oauth/authorize redirect ({spa}/#/oauth/consent?request_id=...). Lives OUTSIDE
 * ProtectedRoute — the session/login/redirect logic is owned by useOauthConsent; this component
 * is presentation only.
 */
export const OauthConsentPage: FC = () => {
  const { t } = useTranslation();
  const { direction } = useRtl();
  const { isMobile } = useIsMobile();
  const {
    pageState,
    errorMessage,
    consentRequest,
    selectedCompanyId,
    setSelectedCompanyId,
    isSubmitting,
    shouldShowFreshLoginNotice,
    scopes,
    isNonProductionLoginColors,
    handleApprove,
    handleDeny,
  } = useOauthConsent();

  const eligibleCompanies = consentRequest?.eligibleCompanies ?? [];

  return (
    <MainLoginContainerStyled dir={direction}>
      {!isMobile && (
        <LeftImageLogin>
          <SVG_MUI.LogoLoginSVG className="logo" />
          <img src={PNG.LoginLogoPNG} alt="logo" />
        </LeftImageLogin>
      )}

      <OauthConsentFormStyled>
        <div className="consentCard">
          {/* Live regions: state changes are announced to assistive technology. */}
          {pageState === "loading" && (
            <div
              className="statusMessage"
              role="status"
              aria-live="polite"
              aria-label={t("general.loading")}
            >
              <CircularProgress size={28} />
            </div>
          )}

          {pageState === "error" && (
            <div className="errorMessage" role="alert">
              {errorMessage}
            </div>
          )}

          {pageState === "redirecting" && (
            <div className="statusMessage" role="status" aria-live="polite">
              {t("oauthConsent.redirecting")}
            </div>
          )}

          {pageState === "denied" && (
            <div className="statusMessage" role="status" aria-live="polite">
              {t("oauthConsent.deniedRedirecting")}
            </div>
          )}

          {pageState === "ready" && consentRequest && (
            <>
              {consentRequest.logoUri && (
                <img
                  className="clientLogo"
                  src={consentRequest.logoUri}
                  alt={consentRequest.clientName}
                />
              )}
              <OauthConsentTitleStyled
                $isNonProductionLoginColors={isNonProductionLoginColors}
              >
                <span className="login-title-row">
                  <span className="login-title-primary">
                    {t("oauthConsent.title")}
                  </span>
                </span>
              </OauthConsentTitleStyled>
              <div className="requestingAccess">
                {t("oauthConsent.requestingAccess", {
                  clientName: consentRequest.clientName,
                })}
              </div>

              <p className="scopesTitle">{t("oauthConsent.scopesTitle")}</p>
              <ul className="scopesList">
                {scopes.includes(OAUTH_SCOPE_READ) && (
                  <li>{t("oauthConsent.scopeRead")}</li>
                )}
                {scopes.includes(OAUTH_SCOPE_WRITE) && (
                  <li>{t("oauthConsent.scopeWrite")}</li>
                )}
              </ul>

              {eligibleCompanies.length > 1 && (
                <div className="companyRow">
                  <span className="companyLabel" id={COMPANY_SELECT_LABEL_ID}>
                    {t("oauthConsent.companyLabel")}
                  </span>
                  <Select
                    size="small"
                    fullWidth
                    sx={textboxLoginSx}
                    labelId={COMPANY_SELECT_LABEL_ID}
                    value={selectedCompanyId ?? ""}
                    onChange={event =>
                      setSelectedCompanyId(Number(event.target.value))
                    }
                    data-e2e="oauth-consent-company-select"
                  >
                    {eligibleCompanies.map(company => (
                      <MenuItem
                        key={company.companyId}
                        value={company.companyId}
                      >
                        {company.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}

              {shouldShowFreshLoginNotice && (
                <div className="freshLoginNotice" role="note">
                  {t("oauthConsent.freshLoginNotice")}
                </div>
              )}

              <div className="actions">
                <MuiButton
                  muiVariant="outlined"
                  muiStyle="secondary"
                  onClick={handleDeny}
                  disabled={isSubmitting}
                  e2eData="oauth-consent-deny"
                >
                  {t("oauthConsent.deny")}
                </MuiButton>
                <MuiButton
                  onClick={handleApprove}
                  loading={isSubmitting}
                  e2eData="oauth-consent-approve"
                >
                  {t("oauthConsent.approve")}
                </MuiButton>
              </div>
            </>
          )}
        </div>
      </OauthConsentFormStyled>

      {!isMobile && (
        <CopyrightLine>
          <div>{t("ui.COPYRIGHT_INFO")} </div>
        </CopyrightLine>
      )}
    </MainLoginContainerStyled>
  );
};
